/* BI reports — the index of Power BI dashboards shared with this account.

   No mockup exists for this screen. Normative rules: docs/design/spec/. The shape is archetype A
   (spec/03#page-archetypes) with nothing to filter and nothing to select: page head → accordion.

   ONE REPORT IS MOUNTED AT A TIME (spec/03#mount-one-section-at-a-time). The page this replaced
   put every report's <iframe> on screen at once — N Power BI sessions, each loading a dataset,
   for a user who is going to read one of them. The accordion is single-open and
   `destroyOnHidden`, so collapsing a panel actually unmounts its frame.

   That is a deliberate exception to rule 3 of that section ("cache once"): a BI embed kept alive
   behind a closed panel keeps polling, so reloading on reopen costs less than the connection we
   would be holding. Everything else about the rule stands — nothing is fetched until opened.

   The open report rides in `?r=<id>`, so a reload and Back land on the same one. */

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button, Collapse, Empty, Flex, Skeleton, Typography } from "antd";
import { BarChartOutlined, ExportOutlined } from "@ant-design/icons";

import { call } from "../../util/action";
import { translate } from "../../util/util";
import { BI_REPORTS, SIDE_NAV } from "../../config/lang";
import "./bi-report.scss";

const View = () => {
  const lang = useSelector(state => state.me?.language || "en");
  const t = key => translate(BI_REPORTS, key, lang);

  const history = useHistory();
  const location = useLocation();
  const openKey = new URLSearchParams(location.search).get("r") || null;

  const [list, setList] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  /* Which report's frame has finished loading, rather than a bare boolean — comparing against the
     open key is what resets the skeleton when the user switches reports, with no effect needed. */
  const [loadedKey, setLoadedKey] = useState(null);

  const load = useCallback(() => {
    setStatus("loading");
    /* `call` answers a failure with `{results: [], count: 0}`, so the array is the success test. */
    call({ method: "GET", url: "reports/" }, r => {
      if (!Array.isArray(r)) return setStatus("error");
      setList(r);
      setStatus("ready");
    });
  }, []);

  useEffect(load, [load]);

  const setOpen = key => {
    const search = key ? `?r=${encodeURIComponent(key)}` : "";
    history.replace(location.pathname + search);
  };

  if (status === "loading") {
    return (
      <Flex vertical gap="middle" className="shell-content bi">
        <Skeleton active paragraph={{ rows: 1 }} />
        <Skeleton active title={false} paragraph={{ rows: 4 }} />
      </Flex>
    );
  }

  const items = list.map(report => ({
    key: String(report.id),
    label: (
      <span className="bi-row">
        <BarChartOutlined className="bi-row__icon" />
        {report.name}
      </span>
    ),
    extra: (
      <Button
        type="text"
        size="small"
        icon={<ExportOutlined />}
        href={report.url}
        target="_blank"
        rel="noopener noreferrer"
        /* The header toggles the panel; the link must not. */
        onClick={event => event.stopPropagation()}>
        {t("open_new_tab")}
      </Button>
    ),
    children: (
      <div className="bi-frame">
        {loadedKey !== String(report.id) && <Skeleton active className="bi-frame__skeleton" paragraph={{ rows: 6 }} />}
        <iframe className="bi-frame__embed" src={report.url} title={report.name} allowFullScreen onLoad={() => setLoadedKey(String(report.id))} />
      </div>
    ),
  }));

  return (
    <Flex vertical gap="middle" className="shell-content bi">
      <Flex vertical>
        <Typography.Title level={1} className="bi-title">
          {translate(SIDE_NAV, "all_reports", lang)}
        </Typography.Title>
        {/* Number kept off the noun (spec/04#counts): "Available: 4", never "4 reports". */}
        <Typography.Paragraph type="secondary" className="bi-sub">
          {status === "ready" && list.length > 0 ? `${t("available")}: ${list.length}` : t("subtitle")}
        </Typography.Paragraph>
      </Flex>

      {status === "error" && (
        <Alert
          type="error"
          showIcon
          message={t("error")}
          action={
            <Button size="small" onClick={load}>
              {t("retry")}
            </Button>
          }
        />
      )}

      {status === "ready" && list.length === 0 && <Empty description={t("empty")} />}

      {items.length > 0 && (
        <Collapse
          accordion
          destroyOnHidden
          className="bi-list"
          activeKey={openKey ? [openKey] : []}
          onChange={keys => setOpen(keys[keys.length - 1] || null)}
          items={items}
        />
      )}
    </Flex>
  );
};

export default View;
